import type { ReactNode } from "react";
import type { DeviceRow } from "./devices.tsx";
import { tagLabel } from "./tableState.ts";

export const MISSING = "—";

export const DEVICE_DETAIL_FIELD_KEYS = [
  "deviceId",
  "name",
  "hostname",
  "status",
  "presence",
  "lastSeen",
  "lastBoot",
  "loggedOnUser",
  "os",
  "platform",
  "osVersion",
  "osArchitecture",
  "agentVersion",
  "minimumAgentVersion",
  "protocolVersion",
  "capabilities",
  "tags",
  "organisation",
  "department",
  "environment",
  "enrolledAt",
  "enrolledBy",
  "approvedAt",
  "approvedBy",
  "lastSeenIp",
  "revokedAt",
  "revokeReason",
  "displays",
  "recordingSelectable",
  "recordingForced",
  "stealthCommand",
  "reasonRequired",
] as const;

export type DeviceDetailFieldKey = (typeof DEVICE_DETAIL_FIELD_KEYS)[number];

export type DeviceDetailField = {
  key: DeviceDetailFieldKey;
  label: string;
  value: string;
};

const SECRET_LABEL = /\b(token|secret|hash|password|credential|enrolment code|private key)\b/i;

export function dialogShowsSecretLikeLabel(text: string): boolean {
  return SECRET_LABEL.test(text);
}

function text(value: string | null | undefined): string {
  if (value === null || value === undefined) return MISSING;
  const trimmed = value.trim();
  return trimmed.length === 0 ? MISSING : trimmed;
}

function yesNo(value: boolean | undefined): string {
  if (value === undefined) return MISSING;
  return value ? "Yes" : "No";
}

function tags(device: DeviceRow): string {
  if (device.tags.length === 0) return "None";
  return device.tags.map(tagLabel).join(", ");
}

function capabilities(device: DeviceRow): string {
  if (device.capabilities === undefined) return MISSING;
  if (device.capabilities.length === 0) return "None";
  return device.capabilities.join(", ");
}

/** Builds dialog rows from the allow-list only. Does not iterate unknown device properties. */
export function deviceDetailFields(device: DeviceRow): DeviceDetailField[] {
  const rows: DeviceDetailField[] = [];
  for (const key of DEVICE_DETAIL_FIELD_KEYS) {
    switch (key) {
      case "deviceId":
        rows.push({ key, label: "Device id", value: device.deviceId });
        break;
      case "name":
        rows.push({ key, label: "Name", value: device.name });
        break;
      case "hostname":
        rows.push({ key, label: "Hostname", value: device.hostname });
        break;
      case "status":
        rows.push({ key, label: "Status", value: device.status });
        break;
      case "presence":
        rows.push({ key, label: "Online", value: device.presence });
        break;
      case "lastSeen":
        rows.push({ key, label: "Last seen", value: device.lastHeartbeatLabel });
        break;
      case "lastBoot":
        rows.push({ key, label: "Last boot", value: device.lastBootLabel });
        break;
      case "loggedOnUser":
        rows.push({ key, label: "Logged-on user", value: device.loggedOnUser });
        break;
      case "os":
        rows.push({ key, label: "OS", value: device.osLabel });
        break;
      case "platform":
        rows.push({ key, label: "Platform", value: text(device.platform) });
        break;
      case "osVersion":
        rows.push({ key, label: "OS version", value: text(device.osVersion) });
        break;
      case "osArchitecture":
        rows.push({ key, label: "Architecture", value: text(device.osArchitecture) });
        break;
      case "agentVersion":
        rows.push({ key, label: "Agent version", value: device.agentVersion });
        break;
      case "minimumAgentVersion":
        rows.push({ key, label: "Minimum agent version", value: text(device.minimumAgentVersion) });
        break;
      case "protocolVersion":
        rows.push({ key, label: "Protocol version", value: text(device.protocolVersion) });
        break;
      case "capabilities":
        rows.push({ key, label: "Capabilities", value: capabilities(device) });
        break;
      case "tags":
        rows.push({ key, label: "Tags", value: tags(device) });
        break;
      case "organisation":
        rows.push({ key, label: "Organisation", value: text(device.organisationName) });
        break;
      case "department":
        rows.push({ key, label: "Department", value: text(device.departmentName) });
        break;
      case "environment":
        rows.push({ key, label: "Environment", value: text(device.environment) });
        break;
      case "enrolledAt":
        rows.push({ key, label: "Enrolled", value: device.createdAtLabel ?? MISSING });
        break;
      case "enrolledBy":
        rows.push({ key, label: "Enrolled by", value: MISSING });
        break;
      case "approvedAt":
        rows.push({ key, label: "Approved", value: device.approvedAtLabel ?? MISSING });
        break;
      case "approvedBy":
        rows.push({ key, label: "Approved by", value: MISSING });
        break;
      case "lastSeenIp":
        rows.push({ key, label: "Last seen IP", value: text(device.lastSeenIp) });
        break;
      case "revokedAt":
        rows.push({ key, label: "Revoked", value: device.revokedAtLabel ?? MISSING });
        break;
      case "revokeReason":
        rows.push({ key, label: "Revoke reason", value: text(device.revokeReason) });
        break;
      case "displays":
        rows.push({ key, label: "Displays", value: device.displaySummary ?? MISSING });
        break;
      case "recordingSelectable":
        rows.push({ key, label: "Recording selectable", value: yesNo(device.recordingSelectable) });
        break;
      case "recordingForced":
        rows.push({ key, label: "Recording required", value: yesNo(device.recordingForced) });
        break;
      case "stealthCommand":
        rows.push({ key, label: "Stealth Command", value: yesNo(device.stealthCommand) });
        break;
      case "reasonRequired":
        rows.push({ key, label: "Reason required", value: yesNo(device.reasonRequired) });
        break;
      default: {
        const _never: never = key;
        throw new Error(`Unhandled device detail field ${_never}`);
      }
    }
  }
  return rows;
}

export function renderDeviceDetailValue(field: DeviceDetailField): ReactNode {
  if (field.key === "os") {
    return <span title={field.value}>{field.value}</span>;
  }
  return field.value;
}
